<template>
	<div class="d-flex flex-column">
		<Banner v-if="banner && !isLoading" :banner="banner" />
		<EventDescription class="mt-5" :event="event" />

		<v-skeleton-loader
			v-if="isLoading"
			v-bind="attrs"
			max-width="555"
			width="100%"
			type="list-item, list-item-three-line, card-heading"
			class="mt-3"
		/>
		<div class="d-flex flex-column" v-else>
			<FilterChipButtons
				:isButtonActive="isButtonActive"
				:chipsList="chipsList"
				:clearFilter="clearFilter"
				@handleChipClick="handleChipClick"
				:useContentAsKey="true"
			/>
			<div v-if="filterModalIsVisible">
				<FilterModal
					:filterModalIsVisible="filterModalIsVisible"
					:currentModalFilter="currentModalFilter"
					:currentModalLabel="currentModalLabel"
					:selectedChips="selectedChips"
					:filters="filters"
					:dateError="date.error"
					:dateOption="date.option"
					:dateRange="date.selectedRange"
					@closeModal="closeModal"
					@applySelection="applySelection"
					@setItem="setItem"
				/>
			</div>
			<div class="mt-2" v-if="!groupedByMonthAndDay.length" style="width: 100%">
				<NewsFeedEmpty
					title="Session is empty at the moment."
					description="Check back later."
				/>
			</div>
			<div v-for="{ date, data } in groupedByMonthAndDay" :key="date">
				<div class="agenda-page__title">
					{{ date }}
				</div>
				<div v-for="(agenda, index) in data" :key="index">
					<router-link
						v-if="agenda.is_extended"
						:to="{
							name: 'event-session-by-id',
							params: {
								id: agenda.events_agenda_id,
							},
						}"
						class="d-flex text-decoration-none"
						style="width: 100%"
					>
						<AgendaCard :agenda-event="agenda" />
					</router-link>
					<AgendaCard v-else :agenda-event="agenda" />
				</div>
			</div>

			<div
				class="d-flex my-9 justify-center home-page__pagination"
				v-if="agendaEvent.length > 0"
			>
				<v-pagination
					color="#F2F2F2"
					:total-visible="10"
					circle
					v-model="filters.page"
					@input="(e) => onPageChange(e)"
					:length="+this.pageCountAgendas"
				/>
			</div>
		</div>
	</div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import AgendaCard from '@/components/Event/AgendaCard'
import NewsFeedEmpty from '@/components/Post/NewsFeedEmpty'
import Dwh from '@/mixins/Dwh'
import EventDescription from '@/components/Event/EventDescription.vue'
import FilterChipButtons from '@/components/FilterChipButtons.vue'
import FilterModal from '@/components/Modals/FilterModal'
import Filter from '@/mixins/Filter'
import Banner from '@/components/Banner'

export default {
	name: 'AgendasPage',
	mixins: [Dwh, Filter],
	components: {
		FilterChipButtons,
		EventDescription,
		AgendaCard,
		NewsFeedEmpty,
		FilterModal,
		Banner,
	},
	methods: {
		...mapActions('event', ['getEventTracks', 'clearEventTracks', 'getEvent']),
		...mapActions('advertisement', ['getBanner', 'clearBanner']),
		...mapActions('list', ['getSortType']),
	},
	created() {
		this.agendaName = this.$route.name
		if (this.$route.params.id !== undefined) {
			this.getEventTracks({ eventId: this.$route.params.id, page: 1 })
			const bannerQuery = {
				places: ['event_agenda'],
				place_sub_id: this.$route.params.id,
				page: this.filters.page,
			}
			if (this.selectedChips.chapters && this.selectedChips.chapters.length) {
				bannerQuery.chapter_id = this.selectedChips.chapters
			}
			this.getBanner(bannerQuery)
		}
		this.getEvent(this.$route.params.id)
		this.getAgendaEvent(this.requestQuery)
		const triggerPayload = {
			category: 'Content',
			action: this.$route.name === 'event-agenda' ? 'view_agenda' : 'view_my_agenda',
			module: 'Events',
		}
		if (this.event) {
			triggerPayload.data = this.event.event_id

			if (this.event.chapter_id) {
				triggerPayload.content_chapter_id = [this.event.chapter_id]
			}
		}
		this.sendTrigger(triggerPayload)
	},
	beforeDestroy() {
		this.cacheCurrentFilters(this.$route.params.id ?? null)
		this.clearEventTracks()
		this.clearBanner()
	},
	computed: {
		...mapGetters('event', [
			'isLoading',
			'pageCountAgendas',
			'agendaEvent',
			'eventTracks',
			'event',
		]),
		...mapGetters('advertisement', ['banner']),
		...mapGetters('list', ['sortType']),
		groupedByMonthAndDay() {
			const result = []
			this.agendaEvent.forEach((event) => {
				const fromDate = new Date(event.events_agenda_from * 1000)
				const month = fromDate.toLocaleString('en-US', { month: 'long' })
				const day = fromDate.getDate()
				const year = fromDate.getFullYear()
				const key = `${month} ${day}, ${year}`

				if (result.length === 0) {
					result.push({
						date: key,
						data: [event],
					})
				} else {
					const lastGroup = result[result.length - 1]
					if (lastGroup.date === key) {
						lastGroup.data.push(event)
					} else {
						result.push({
							date: key,
							data: [event],
						})
					}
				}
			})
			return result.map(({ date, data }) => ({ date, data }))
		},
		isMyAgendaPage() {
			return this.$route.name === 'my-agenda-events'
		},
	},

	data() {
		return {
			attrs: {
				elevation: 0,
			},
			agendaName: null,
		}
	},
}
</script>

<style lang="scss">
.agenda-page {
	background: #f1f1f1 !important;

	&__title {
		color: rgb(74, 74, 74);
		font-size: 18px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		margin-bottom: 10px;
		margin-left: 1px;
		margin-top: 12px;
	}

	.post {
		&__search {
			background: white;
			height: 54px;
			border-radius: 10px;
			border: 1px solid rgb(230, 230, 230);
			max-width: 600px;
		}

		@media screen and (max-width: 1264px) {
			&__search {
				max-width: none;
				width: 100%;
			}
		}
	}
}

@media screen and (min-width: 1200px) {
	.agenda-page-container {
		max-width: 100%;
		width: 100%;
	}
}

@media screen and (min-width: 1320px) {
	.agenda-page-container {
		max-width: 600px;
	}
}
</style>
